/**
 * Canonical SVG document handling: preserve full source files, trim via root
 * viewBox, and derive one display document for measure + render.
 */

import { translatePathData } from './svgPathTransform';

export type SvgTrimViewBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

const SVG_NS = 'http://www.w3.org/2000/svg';

export function parseSvgViewBox(
  svgMarkup: string,
  fallbackW: number,
  fallbackH: number
): SvgTrimViewBox {
  const viewBoxMatch = svgMarkup.match(/viewBox=["']([^"']+)["']/i);
  if (viewBoxMatch) {
    const parts = viewBoxMatch[1].split(/[\s,]+/).map(Number);
    if (parts.length >= 4 && parts.every((n) => Number.isFinite(n))) {
      return { x: parts[0], y: parts[1], width: parts[2], height: parts[3] };
    }
  }

  const wMatch = svgMarkup.match(/\bwidth=["']([\d.]+)/i);
  const hMatch = svgMarkup.match(/\bheight=["']([\d.]+)/i);
  return {
    x: 0,
    y: 0,
    width: wMatch ? parseFloat(wMatch[1]) : fallbackW,
    height: hMatch ? parseFloat(hMatch[1]) : fallbackH,
  };
}

function setOrReplaceSvgAttribute(openTag: string, name: string, value: string): string {
  const re = new RegExp(`\\b${name}=["'][^"']*["']`, 'i');
  if (re.test(openTag)) {
    return openTag.replace(re, `${name}="${value}"`);
  }
  return openTag.replace(/<svg\b/i, `<svg ${name}="${value}"`);
}

/** Patch root viewBox/width/height on a full SVG document (namespaces preserved). */
function patchSvgRootViewBox(svgSource: string, trim: SvgTrimViewBox): string {
  const trimmed = svgSource.trim();
  const openTagMatch = trimmed.match(/<svg\b[^>]*>/i);
  if (!openTagMatch) return trimmed;

  const viewBoxValue = `${trim.x} ${trim.y} ${trim.width} ${trim.height}`;
  let openTag = openTagMatch[0];
  openTag = setOrReplaceSvgAttribute(openTag, 'viewBox', viewBoxValue);
  openTag = setOrReplaceSvgAttribute(openTag, 'width', String(trim.width));
  openTag = setOrReplaceSvgAttribute(openTag, 'height', String(trim.height));

  return trimmed.replace(openTagMatch[0], openTag);
}

/** Single derivation used for perimeter measurement, stroke width, and nested render. */
export function buildDisplaySvg(svgSource: string, trim: SvgTrimViewBox): string {
  return patchSvgRootViewBox(svgSource, trim);
}

export function getTrimViewBoxFromItem(item: {
  trimViewBoxX?: number;
  trimViewBoxY?: number;
  viewBoxWidth: number;
  viewBoxHeight: number;
}): SvgTrimViewBox {
  return {
    x: item.trimViewBoxX ?? 0,
    y: item.trimViewBoxY ?? 0,
    width: item.viewBoxWidth,
    height: item.viewBoxHeight,
  };
}

function serializeNode(node: Node): string {
  return new XMLSerializer().serializeToString(node);
}

function isAllowedRenderElement(element: Element): boolean {
  const ns = element.namespaceURI;
  return ns === SVG_NS || ns === null;
}

/** Root-level SVG children for injection into the editor item wrapper (editor metadata excluded). */
export function extractSvgInnerContent(svgMarkup: string): string {
  try {
    const doc = new DOMParser().parseFromString(svgMarkup, 'image/svg+xml');
    if (doc.querySelector('parsererror')) return '';

    return Array.from(doc.documentElement.children)
      .filter(isAllowedRenderElement)
      .map(serializeNode)
      .join('');
  } catch {
    return '';
  }
}

export function isSvgUrl(url: string): boolean {
  return /\.svg($|[?#])/i.test(url) || url.startsWith('data:image/svg+xml');
}

export async function fetchSvgSource(url: string): Promise<string | null> {
  if (!isSvgUrl(url)) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    return await response.text();
  } catch {
    return null;
  }
}

export async function resolveImageSvgSource(input: {
  svgSource?: string;
  url: string;
}): Promise<string | null> {
  const inline = input.svgSource?.trim();
  if (inline) return inline;
  return fetchSvgSource(input.url);
}

/** Minimal SVG document for text glyph measure/render (path already in viewBox space). */
export function buildTextDisplaySvg(input: {
  pathData: string;
  trimViewBoxX?: number;
  trimViewBoxY?: number;
  viewBoxWidth: number;
  viewBoxHeight: number;
}): string {
  const {
    pathData,
    trimViewBoxX = 0,
    trimViewBoxY = 0,
    viewBoxWidth,
    viewBoxHeight,
  } = input;
  return (
    `<svg xmlns="${SVG_NS}" viewBox="${trimViewBoxX} ${trimViewBoxY} ${viewBoxWidth} ${viewBoxHeight}" ` +
    `width="${viewBoxWidth}" height="${viewBoxHeight}">` +
    `<path fill="#000" d="${pathData}"/>` +
    `</svg>`
  );
}

/** Bake legacy pathOffset into pathData for display/measure (no SVG transform). */
export function resolveTextDisplayPathData(item: {
  pathData?: string;
  pathOffsetX?: number;
  pathOffsetY?: number;
}): string {
  const pathData = item.pathData?.trim() ?? '';
  if (!pathData) return '';
  const dx = item.pathOffsetX ?? 0;
  const dy = item.pathOffsetY ?? 0;
  return translatePathData(pathData, dx, dy);
}

export function buildTextDisplaySvgFromItem(item: {
  pathData?: string;
  pathOffsetX?: number;
  pathOffsetY?: number;
  trimViewBoxX?: number;
  trimViewBoxY?: number;
  viewBoxWidth: number;
  viewBoxHeight: number;
}): string | null {
  const pathData = resolveTextDisplayPathData(item);
  if (!pathData) return null;
  const { viewBoxWidth, viewBoxHeight } = item;
  if (!(viewBoxWidth > 0) || !(viewBoxHeight > 0)) return null;
  return buildTextDisplaySvg({
    pathData,
    trimViewBoxX: item.trimViewBoxX,
    trimViewBoxY: item.trimViewBoxY,
    viewBoxWidth,
    viewBoxHeight,
  });
}
